import { setLevel1Records, setLevel2Records, setLevel3Records, setLevel4Records } from "../../construction-costs/components/construction-cost-record-form-dialog/construction-cost-record-rows";
import { CostAssessmentHeaderRow, CostAssessmentRecordRow, CostAssessmentValueRow, LandInput } from "../types";
import { toCostAssessmentRecord } from "./mapper";

// Resets
export function resetAllRecord(
  records: CostAssessmentRecordRow[]
): CostAssessmentRecordRow[] {
  return records.map(item => {
    if (item.kind === 'header') return item;
    return _resetRecord(item)
  })
}

export function resetRecord(
  records: CostAssessmentRecordRow[],
  record: CostAssessmentValueRow
): CostAssessmentRecordRow[] {
  return records.map(item => {
    if (item.kind === 'header') return item
    if (item.code !== record.code) return item;
    return _resetRecord(item)
  })
}

function _resetRecord(record: CostAssessmentValueRow): CostAssessmentValueRow {
  return {
    ...record,
    useful_life: 0,
    quantity: 0,
    unit: null,
    unit_rate: 0
  }
}

// Toggle States
export function toggleRecordAvailability(
  records: CostAssessmentRecordRow[],
  record: CostAssessmentValueRow,
  disabled: boolean
): CostAssessmentRecordRow[] {
  return records.map(item => {
    if (item.kind === 'header') return item
    if (item.code !== record.code) return item
    return {
      ...item,
      isRemoved: disabled
    }
  })
}

export function toggleRecordVisibility(
  records: CostAssessmentRecordRow[],
  record: CostAssessmentHeaderRow,
): CostAssessmentRecordRow[] {
  return records.map(item => {
    if (item.code === record.code) {
      return {
        ...item,
        isOpen: !record.isOpen
      }
    }
    if (item.code.startsWith(record.code)) {
      return {
        ...item,
        isHidden: record.isOpen
      }
    }
    return item
  })
}

// Value changes
export function unitRateChange(
  records: CostAssessmentRecordRow[],
  index: number,
  value: number
): CostAssessmentRecordRow[] {
  return records.map((item, i) => {
    if (item.kind === 'header') return item;
    if (index !== i) return item;
    return {
      ...item,
      unit_rate: value
    }
  })
}

export function unitChange(
  records: CostAssessmentRecordRow[],
  index: number,
  value: number
): CostAssessmentRecordRow[] {
  return records.map((item, i) => {
    if (item.kind === 'header') return item;
    if (index !== i) return item;
    return {
      ...item,
      unit: value
    }
  })
}

export function quantityChange(
  records: CostAssessmentRecordRow[],
  index: number,
  value: number
): CostAssessmentRecordRow[] {
  return records.map((item, i) => {
    if (item.kind === 'header') return item;
    if (index !== i) return item;
    return {
      ...item,
      quantity: value
    }
  })
}

export function usefulLifeChange(
  records: CostAssessmentRecordRow[],
  index: number,
  value: number
): CostAssessmentRecordRow[] {
  return records.map((item, i) => {
    if (item.kind === 'header') return item;
    if (index !== i) return item;
    return {
      ...item,
      useful_life: value
    }
  })
}

// Use land value input & Use size as quantity
export function useLandValueInput(
  records: CostAssessmentRecordRow[],
  level: number,
  landInput: LandInput
): CostAssessmentRecordRow[] {
  if (level === 2) return changeToLandInput(records, '1', landInput);
  if (level === 3) return changeToLandInput(records, '1.01', landInput);
  if (level === 4) return changeToLandInput(records, '1.01.010', landInput);
  return [...records]
}

function changeToLandInput(
  records: CostAssessmentRecordRow[],
  code: string,
  landInput: LandInput
): CostAssessmentRecordRow[] {
  return records.map(item => {
    if (item.kind === 'header') return item
    if (item.code !== code) return item
    return {
      ...item,
      quantity: landInput.size,
      unit: landInput.unit,
      unit_rate: landInput.price
    }
  })
}

export function useSizeAsQuantity(
  records: CostAssessmentRecordRow[],
  size: number
): CostAssessmentRecordRow[] {
  return records.map(item => {
    if (item.kind === 'header') return item
    return {
      ...item,
      quantity: size
    }
  })
}

// ICMS records
export function setICMSRecords(level: number): CostAssessmentRecordRow[] {
  let records = []
  if (level == 2) {
    records = setLevel2Records()
  }
  if (level == 3) {
    records = setLevel3Records()
  }
  if (level == 4) {
    records = setLevel4Records()
  }
  if (level == 1) {
    records = setLevel1Records([{value: 1, label: 'Building'}], 1)
  }

  return toCostAssessmentRecord(records)
}

export function compareRecords(
  oldRecords: CostAssessmentRecordRow[],
  newRecords: CostAssessmentRecordRow[]
): boolean {
  return areRecordsEqual(oldRecords, newRecords)
}

function compareRecord(
  oldRecord: CostAssessmentRecordRow,
  newRecord: CostAssessmentRecordRow
): boolean {
  if (oldRecord.kind !== newRecord.kind) return false;
  if (oldRecord.kind === 'header' && newRecord.kind === 'header')  return true;
  if (oldRecord.kind === 'value' && newRecord.kind === 'value') {
    return (
      oldRecord.useful_life === newRecord.useful_life &&
      oldRecord.unit === newRecord.unit
    )
  }
  return false
}

/**
 * Deeply compares two individual CostAssessmentRecordRow objects, ignoring the 'tooltips' property.
 * @returns `true` if the records are identical, `false` otherwise.
 */
function areSingleRecordsEqual(
  oldRecord: CostAssessmentRecordRow,
  newRecord: CostAssessmentRecordRow
): boolean {
  // 1. Different kinds means they are not equal
  if (oldRecord.kind !== newRecord.kind) {
    return false;
  }

  // 2. Compare common BaseRow properties
  if (
    oldRecord.code !== newRecord.code ||
    oldRecord.category !== newRecord.category ||
    oldRecord.isHidden !== newRecord.isHidden ||
    oldRecord.backgroundColor !== newRecord.backgroundColor
  ) {
    return false;
  }

  // 3. Compare kind-specific properties
  switch (oldRecord.kind) {
    case 'header': {
      const newHeaderRecord = newRecord as CostAssessmentHeaderRow;
      return oldRecord.isOpen === newHeaderRecord.isOpen && oldRecord.depreciation === newHeaderRecord.depreciation;
    }
    case 'value': {
      const newValueRecord = newRecord as CostAssessmentValueRow;
      // Compare all properties of a 'value' row EXCEPT for 'tooltips'.
      return (
        oldRecord.useful_life === newValueRecord.useful_life &&
        oldRecord.quantity === newValueRecord.quantity &&
        oldRecord.unit === newValueRecord.unit &&
        oldRecord.unit_rate === newValueRecord.unit_rate &&
        oldRecord.isRemoved === newValueRecord.isRemoved &&
        oldRecord.depreciation === newValueRecord.depreciation
      );
    }
    default:
      // Should not be reachable with valid types
      return false;
  }
}

/**
 * Deeply compares two arrays of CostAssessmentRecordRow for equality, ignoring the 'tooltips' property.
 * This function is designed to be used as the comparator in operators like
 * RxJS's distinctUntilChanged.
 *
 * @param oldRecords The previous array of records.
 * @param newRecords The new array of records.
 * @returns `true` if the arrays are deeply equal, `false` otherwise.
 */
export function areRecordsEqual(
  oldRecords: CostAssessmentRecordRow[],
  newRecords: CostAssessmentRecordRow[]
): boolean {
  // 1. Trivial check for same instance
  if (oldRecords === newRecords) {
    return true;
  }

  // 2. Arrays of different lengths are not equal
  if (oldRecords.length !== newRecords.length) {
    return false;
  }

  // 3. Compare each record in the array
  for (let i = 0; i < oldRecords.length; i++) {
    if (!areSingleRecordsEqual(oldRecords[i], newRecords[i])) {
      // If any record is different, the arrays are not equal
      return false;
    }
  }

  // 4. If all records are the same, the arrays are equal
  return true;
}